import React, { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { dataActions } from '@/store/actions/data.actions';
import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import useWindowIsMobile from '@/utils/hooks/useWindowIsMobile';
import { CanHandleDepartments } from '@/utils/permissions/permissions';

import CustomSelect from '@/views/components/CustomSelect/CustomSelect';

import LettersNameAutoSuggest from '@/views/components/AutoSuggestInput/LettersNameAutoSuggest';
import LettersDeptAutoSuggest from '@/views/components/AutoSuggestInput/LettersDeptAutoSuggest';

import SearchInput from '@/views/components/SearchInput/SearchInput';

import styles from '@/views/pages/Letters/Letters.module.scss';

const LettersSearchFilters = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { dict, mode } = useSelector((state) => state.app);
	const user = useSelector((state) => state.auth.user);

	// load title types here - it will be needed in LettersItem
	const { recipients, versions, titleTypes, categories } = useSelector(state => state.data);
	const [ selectVersions, setSelectVersions ] = useState([]);
	const [ selectRecipients, setSelectRecipients ] = useState([]);
	const [ selectCategories, setSelectCategories ] = useState([]);

	const [ versionsStartValue, setVersionsStartValue ] = useState({ label: dict.letters.allVersions, value: 'none' });
	const [ recipientsStartValue, setRecipientsStartValue ] = useState({ label: dict.letters.allRecipients, value: 'none' });
	const [ categoryStartValue, setCategoryStartValue ] = useState({ label: dict.letters.allCategories, value: 'none' });
	const [ departmentStartId, setDepartmentStartId ] = useState(0);

	const [ searchedQuery, setSearchedQuery ] = useState('');
	const [ query, setQuery ] = useState('');

	const [ hideFilters, setHideFilters ] = useState(false);

	const isMobile = useWindowIsMobile(767);

	const userRole = user && user.role;

	const resetAllFilters = () => {
		setVersionsStartValue({ label: dict.letters.allVersions, value: 'none' });
		setRecipientsStartValue({ label: dict.letters.allRecipients, value: 'none' });
		setCategoryStartValue({ label: dict.letters.allCategories, value: 'none' });
		setDepartmentStartId(null);
	};

	const searchQuery = (searchString = null) => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);
		if (searchString === null || searchString.length === 0) {
			if (Object.keys(copiedSearchParams).indexOf('q') !== -1) {
				delete copiedSearchParams.q;
				if (copiedSearchParams.ord === '-accuracy') { copiedSearchParams.ord = '-modifiedOn'; }
				navigate(`${location.pathname}?${parseObjectToQuery(copiedSearchParams)}`);
			}
		} else {
			resetAllFilters();
			delete copiedSearchParams.ord;
			navigate(`${location.pathname}?q=${encodeURIComponent(searchString)}&ord=-accuracy&page=1`);
		}
	};

	useEffect(() => {
		if (!versions) dispatch(dataActions.getVersions());
		if (!recipients) dispatch(dataActions.getRecipients());
		if (!titleTypes) dispatch(dataActions.getTitleTypes());
		if (!categories) dispatch(dataActions.getLetterCategories());
		const qu = queryString.parse(location.search);
		if (Object.keys(qu).indexOf('q') !== -1) {
			setQuery(qu.q);
			setSearchedQuery(qu.q);
		}
		// eslint-disable-next-line
    }, []);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);
		if (copiedSearchParams.q) {
			setQuery(copiedSearchParams.q);
			setSearchedQuery(copiedSearchParams.q);
		} else {
			setQuery("");
			setSearchedQuery("");
		}
	}, [ location ]);

	useEffect(() => {
		if (recipients) {
			const qu = queryString.parse(location.search);
			let urlRecipient = null;
			if (Object.keys(qu).indexOf('recipient') !== -1) {
				// there is recipient in url, set start value of recipient select
				urlRecipient = qu.recipient;
			}
			setSelectRecipients([ { label: dict.letters.allRecipients, value: 'none' },
				...recipients.map(item => {
					const setItem = { value: item.code, label: item.label };
					if (urlRecipient && item.code === urlRecipient) {
						setRecipientsStartValue(setItem);
					}
					return setItem;
				},
				) ]);
		}
		// eslint-disable-next-line
    }, [recipients]);

	useEffect(() => {
		if (versions) {
			const qu = queryString.parse(location.search);
			let urlVersion = null;
			if (Object.keys(qu).indexOf('version') !== -1) {
				// there is version in url, set start value of recipient select
				urlVersion = qu.version;
			}
			setSelectVersions([ { label: dict.letters.allVersions, value: 'none' },
				...versions.map(item => {
					const setItem = { value: item, label: dict.versions[item.replace("-", "")] };
					if (urlVersion && item === urlVersion) {
						setVersionsStartValue(setItem);
					}
					return setItem;
				},
				) ]);
		}
		// eslint-disable-next-line
    }, [versions]);

	useEffect(() => {
		if (categories) {
			const qu = queryString.parse(location.search);
			let urlCategory = null;
			if (Object.keys(qu).indexOf('category') !== -1) {
				// there is version in url, set start value of recipient select
				urlCategory = qu.category;
			}
			setSelectCategories([ { label: dict.letters.allCategories, value: 'none' },
				...categories.map(item => {
					const setItem = { value: item.code, label: item.label };
					if (urlCategory && item.code === urlCategory) {
						setCategoryStartValue(setItem);
					}
					return setItem;
				},
				) ]);
		}
	}, [ categories ]);

	const onSelectVersion = ({ value, label }) => {
		// eslint-disable-next-line no-extra-boolean-cast
		if(Boolean(sessionStorage.getItem('firstLoginDefaultVersion'))) {
			sessionStorage.removeItem("firstLoginDefaultVersion");
		}
		setVersionsStartValue({ value, label });
		const qu = queryString.parse(location.search);
		if (value === 'none') {
			if (Object.keys(qu).indexOf('version') !== -1) {
				delete qu.version;
				navigate(`${location.pathname}?${parseObjectToQuery(qu)}`);
			}
		} else {
			navigate(`${location.pathname}?${parseObjectToQuery({ ...qu, version: value })}`);
		}
	};
	const onSelectCategory = ({ value, label }) => {
		setCategoryStartValue({ value, label });
		const qu = queryString.parse(location.search);
		if (value === 'none') {
			if (Object.keys(qu).indexOf('category') !== -1) {
				delete qu.category;
				navigate(`${location.pathname}?${parseObjectToQuery(qu)}`);
			}
		} else {
			navigate(`${location.pathname}?${parseObjectToQuery({ ...qu, category: value })}`);
		}
	};
	useEffect(() => {
		if(queryString.parse(location.search)?.department) {
			setDepartmentStartId(queryString.parse(location.search)?.department || 0);
		}

		// eslint-disable-next-line
	}, [ location.search ]);

	const onSelectRecipient = ({ value, label }) => {
		setRecipientsStartValue({ value, label });
		const qu = queryString.parse(location.search);
		if (value === 'none') {
			if (Object.keys(qu).indexOf('recipient') !== -1) {
				delete qu.recipient;
				navigate(`${location.pathname}?${parseObjectToQuery(qu)}`);
			}
		} else {
			navigate(`${location.pathname}?${parseObjectToQuery({ ...qu, recipient: value })}`);
		}
	};

	return (
		<section className={ styles['letters__searchbar'] }>
			<SearchInput
				label={ dict.letters.searchLabel }
				placeholder={ "" }
				searchedValue={ searchedQuery }
				value={ query }
				setValue={ setQuery }
				searchFunction={ searchQuery }
			/>
			<div className={ styles['letters__searchbar--item'] }>
				{(!hideFilters || !isMobile) && <div className={ `${styles['letters__searchbar--filters']} ${!CanHandleDepartments(userRole, mode) ? styles['letters__searchbar--filters-standalone'] : null }` }>
					{CanHandleDepartments(userRole, mode) && <LettersDeptAutoSuggest
						label={ dict.letters.searchByDepartment }
						placeholder={ dict.letters.autocompletePlaceholder }
						tooltipIcon={ dict.letters.defaultDeptTooltip }
						startDepartmentId={ departmentStartId }
					/>}

					{selectRecipients && (
						<CustomSelect
							label={ dict.letters.recipient }
							options={ selectRecipients }
							value={ recipientsStartValue }
							onChange={ onSelectRecipient }
							placeholder={ dict.letters.recipient }
						/>
					)}

					<LettersNameAutoSuggest
						label={ dict.letters.searchByInitiator }
						placeholder={ dict.letters.autocompletePlaceholder }
					/>
					{selectVersions && (
						<CustomSelect
							label={ dict.letters.version }
							options={ selectVersions }
							value={ versionsStartValue }
							onChange={ onSelectVersion }
						/>
					)}
					{selectCategories && (
						<CustomSelect
							label={ dict.letter.categories.category }
							options={ selectCategories }
							value={ categoryStartValue }
							onChange={ onSelectCategory }
							placeholder={ dict.letter.categories.category }
						/>
					)}
				</div>}
				{isMobile && <div
					className={ styles['letters__searchbar--hide-button'] }
					onClick={ () => {
						setHideFilters(!hideFilters);
					} }
				>
					{ !hideFilters ?
						<>Ukryj filtry <i className="icon-arrow-up-full" /></> :
						<>Pokaż filtry <i className="icon-arrow-down-full" /></>
					}
				</div>}
			</div>
		</section>
	);
};

export default memo(LettersSearchFilters);
